import React from 'react'
import styles from './PreviewModal.module.scss'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { useAuthStatus } from '../../utils'
import { formatIncentiveTaxOptionLabel, INCENTIVE_BASIS_LABEL } from '../../utils/incentiveDisplay'
import axios from 'axios'
import { useState } from 'react'
import { useEffect, useRef } from 'react'

/** JobPostPeriodType: JSON 배열 문자열 또는 배열 */
function parsePeriodType(periodType) {
	if (periodType == null || periodType === '') return []
	if (Array.isArray(periodType)) return periodType
	if (typeof periodType === 'string') {
		try {
			const parsed = JSON.parse(periodType)
			return Array.isArray(parsed) ? parsed : [periodType]
		} catch {
			return [periodType]
		}
	}
	return []
}

function formatContractDate(value) {
	if (value == null || value === '') return ''
	if (typeof value === 'number' && Number.isFinite(value)) {
		const d = new Date(value)
		return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
	}
	const d = value instanceof Date ? value : new Date(value)
	if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
	const s = String(value)
	return s.length >= 10 ? s.slice(0, 10) : s
}

function tourContractRangeText(selectedToursData) {
	if (!selectedToursData?.length) return null
	const starts = selectedToursData
		.map((t) => new Date(t.startDate ?? t.startedAt))
		.filter((d) => !Number.isNaN(d.getTime()))
	const ends = selectedToursData
		.map((t) => new Date(t.endDate ?? t.endedAt))
		.filter((d) => !Number.isNaN(d.getTime()))
	if (!starts.length || !ends.length) return null
	const minT = Math.min(...starts.map((d) => d.getTime()))
	const maxT = Math.max(...ends.map((d) => d.getTime()))
	return `${formatContractDate(minT)} ~ ${formatContractDate(maxT)}`
}

function periodHalfLabel(periodParts) {
	const hasFirst = periodParts.includes('first-half')
	const hasLast = periodParts.includes('last-half')
	if (hasFirst && hasLast) return '상반기 / 하반기'
	if (hasFirst) return '상반기'
	if (hasLast) return '하반기'
	return ''
}

function periodNoTourEndDate(selectedYear, periodParts) {
	const raw = selectedYear != null ? String(selectedYear).replace(/^"|"$/g, '') : ''
	const yearNum = Number.parseInt(raw, 10)
	if (!Number.isFinite(yearNum)) return ''
	if (periodParts.includes('first-half') && periodParts.includes('last-half'))
		return `${yearNum}-12-31`
	if (periodParts.includes('first-half')) return `${yearNum}-06-30`
	if (periodParts.includes('last-half')) return `${yearNum}-12-31`
	return ''
}

const PreviewModal = ({ isOpen, onClose, writtenData }) => {
	const [authStatus] = useAuthStatus()
	const [salaryDisplayContent, setSalaryDisplayContent] = React.useState(null)
	const [domesticOverseasSwapContent, setDomesticOverseasSwapContent] = useState(true)

	console.log(authStatus)

	const modalRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			// 미리보기 버튼 클릭은 무시
			if (
				event.target.closest('button') &&
				event.target.closest('button').textContent === '미리보기'
			) {
				return
			}

			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen, onClose])

	React.useEffect(() => {
		if (!isOpen) return

		const fetchSalaryDisplay = async () => {
			const content = await renderSalaryDisplay()
			setSalaryDisplayContent(content)
		}
		fetchSalaryDisplay()
	}, [isOpen])

	if (!isOpen) return null

	// 천원단위 포맷팅 함수
	const formatNumber = (num) => {
		if (!num) return '0'
		return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}

	// 날짜에서 년도 앞 두 글자 제거 함수
	const formatYear = (dateString) => {
		if (!dateString) return ''
		// YYYY-MM-DD 형식에서 YYYY를 YY로 변환 (앞 두 글자 제거)
		return dateString.replace(/^\d{2}(\d{2}-)/, '$1')
	}

	// 투어 데이터 집합 처리 함수
	const processTourDataCategory = (selectedToursData) => {
		if (!selectedToursData || selectedToursData.length === 0) return []

		// 투어 타입별 집합 (중복 제거)
		const types = [...new Set(selectedToursData.map((tour) => tour.category))]

		return types
	}

	const renderSalaryDisplay = async () => {
		const dataFromThirdPage = JSON.parse(sessionStorage.getItem('ThirdPageData') || '{}')

		if (dataFromThirdPage.isGeneralContract) {
			const periodSalary = dataFromThirdPage.generalFormData?.periodSalary || '0'
			return (
				<div className={styles.periodSalaryDisplayContainer}>
					<span className={styles.periodSalaryDisplayIcon}>기간급여액</span>
					<span className={styles.moneyDisplay}>{formatNumber(periodSalary)} 원</span>
				</div>
			)
		} else if (dataFromThirdPage.isDomesticContract) {
			const cutPass = dataFromThirdPage.domesticFormData?.cutPass || '0'
			const cutOut = dataFromThirdPage.domesticFormData?.cutOut || '0'
			return (
				<div className={styles.cutDisplayContainer}>
					<div className={styles.cutPassDisplayContainer}>
						<span className={styles.cutPassDisplayIcon}>컷통과</span>
						<span className={styles.cutMoneyDisplay}>
							<span className={styles.moneyDisplayText}>{formatNumber(cutPass)}</span>
							<span className={styles.moneyDisplayText}>원</span>
						</span>
					</div>
					<div className={styles.cutOutDisplayContainer}>
						<span className={styles.cutOutDisplayIcon}>컷탈락</span>
						<span className={styles.cutMoneyDisplay}>
							<span className={styles.moneyDisplayText}>{formatNumber(cutOut)}</span>
							<span className={styles.moneyDisplayText}>원</span>
						</span>
					</div>
				</div>
			)
		} else if (dataFromThirdPage.isOverseasContract) {
			const cutPass = dataFromThirdPage.overseasFormData?.cutPass || '0'
			const cutOut = dataFromThirdPage.overseasFormData?.cutOut || '0'
			return (
				<div className={styles.cutDisplayContainer}>
					<div className={styles.cutPassDisplayContainer}>
						<span className={styles.cutPassDisplayIcon}>컷통과</span>
						<span className={styles.cutMoneyDisplay}>
							<span className={styles.moneyDisplayText}>{formatNumber(cutPass)}</span>
							<span className={styles.moneyDisplayText}>원</span>
						</span>
					</div>
					<div className={styles.cutOutDisplayContainer}>
						<span className={styles.cutOutDisplayIcon}>컷탈락</span>
						<span className={styles.cutMoneyDisplay}>
							<span className={styles.moneyDisplayText}>{formatNumber(cutOut)}</span>
							<span className={styles.moneyDisplayText}>원</span>
						</span>
					</div>
				</div>
			)
		}
	}

	const setDataForRender = () => {
		const { jobPostTitle, jobPostBrief } = writtenData

		const dataFromThirdPage = JSON.parse(sessionStorage.getItem('ThirdPageData') || '{}')

		console.log('writtenData:', writtenData)
		console.log('dataFromThirdPage:', dataFromThirdPage)

		const setMainTitle = () => {
			console.log('jobPostTitle:', jobPostTitle)
			console.log('jobPostContractType:', dataFromThirdPage?.contractType)

			if (dataFromThirdPage?.contractType === 'short-contract') {
				return '[단기계약] ' + (jobPostTitle || '제목 없음')
			} else if (dataFromThirdPage?.contractType === 'long-contract') {
				return '[장기계약] ' + (jobPostTitle || '제목 없음')
			} else if (
				dataFromThirdPage?.contractType &&
				dataFromThirdPage.contractType.includes('period')
			) {
				return '[기간계약] ' + (jobPostTitle || '제목 없음')
			}
			return jobPostTitle || '제목 없음'
		}

		const mainTitle = setMainTitle()
		console.log('mainTitle:', mainTitle)

		// 투어 데이터 집합 처리
		const tourCategory = processTourDataCategory(dataFromThirdPage?.selectedToursData)

		const data = {
			jobPostTitle: jobPostTitle,
			jobPostBrief: jobPostBrief,
			jobPostContractType: dataFromThirdPage?.contractType,
			isOverseas: dataFromThirdPage?.isOverseasContract,
			isDomestic: dataFromThirdPage?.isDomesticContract,
			domesticFormData: dataFromThirdPage?.domesticFormData,
			overseasFormData: dataFromThirdPage?.overseasFormData,
			selectedToursData: dataFromThirdPage?.selectedToursData,
			periodType: dataFromThirdPage?.periodType,
			isGeneralContract: dataFromThirdPage?.isGeneralContract,
			generalFormData: dataFromThirdPage?.generalFormData,
			userAssociation: dataFromThirdPage?.userAssociation,
			selectedYear: dataFromThirdPage?.selectedYear,

			tourCategory: tourCategory,
			mainTitle: mainTitle,
		}

		console.log('dataForRender:', data)

		return data
	}
	const dataForRender = setDataForRender()

	return (
		<div
			className={styles.previewModalContainer}
			ref={modalRef}
			onClick={(e) => e.stopPropagation()}>
			<div className={styles.modalTopBar}>
				<span className={styles.time}>방금 전</span>
				<button
					type="button"
					className={styles.closeIconButton}
					onClick={onClose}
					aria-label="닫기">
					<XMarkIcon className={styles.XMark} />
				</button>
			</div>
			<div className={styles.mainHeader}>
				<div
					className={`${styles.mainTitle} ${!dataForRender.jobPostTitle || dataForRender.mainTitle === '제목 없음' ? styles.mainTitleEmpty : ''}`}>
					{dataForRender.mainTitle}
				</div>
			</div>
			<div className={styles.salaryDisplayContainer}>{salaryDisplayContent}</div>
			<div className={styles.jobPostIntroHeader}>공고소개</div>
			<div className={styles.jobPostIntroContent}>{dataForRender.jobPostBrief}</div>
			<div className={styles.basicContractInfoHeader}>기본 계약정보</div>
			<div className={styles.basicContractInfoTourType}>
				<div className={styles.basicLabel}>투어종류</div>
				<div className={styles.basicContent}>
					{dataForRender.tourCategory?.length === 1
						? dataForRender.tourCategory[0]
						: dataForRender.tourCategory?.join(', ')}
				</div>
			</div>
			<div className={styles.basicContractInfoContractType}>
				<div className={styles.basicLabel}>계약유형</div>
				<div className={styles.basicContent}>
					{dataForRender.jobPostContractType === 'short-contract'
						? '단기계약'
						: dataForRender.jobPostContractType === 'long-contract'
							? '장기계약'
							: '기간계약'}
				</div>
			</div>
			<div
				className={
					dataForRender.isGeneralContract
						? styles.generalContractPeriodContainer
						: styles.nonGeneralContractPeriodContainer
				}>
				<div className={styles.periodLabel}>계약기간</div>
				<div className={styles.periodContent}>
					{(() => {
						const periodParts = parsePeriodType(dataForRender.periodType)
						const half = periodHalfLabel(periodParts)
						const tourRange = tourContractRangeText(dataForRender.selectedToursData)

						if (dataForRender.isGeneralContract) {
							const endHint = periodNoTourEndDate(
								dataForRender.selectedYear,
								periodParts,
							)
							return (
								<>
									{half ? (
										<>
											{half}
											<br />
										</>
									) : null}
									{tourRange ? (
										tourRange
									) : endHint ? (
										<>계약일 ~ {endHint}</>
									) : null}
								</>
							)
						}

						if (tourRange) {
							return tourRange
						}
						return ''
					})()}
				</div>
			</div>
			<div className={styles.tourListContainer}>
				<div className={styles.tourListLabel}>대회목록</div>
				<div className={styles.tourListContent}>
					{(!dataForRender.selectedToursData ||
						dataForRender.selectedToursData.length === 0) &&
						'미정'}
					{dataForRender.selectedToursData?.length > 0 &&
						dataForRender.selectedToursData.map((tour, index) => (
							<div key={index} className={styles.tourListItemContainer}>
								<div className={styles.tourListItemIndex}>({index + 1})</div>
								<div className={styles.tourListItemMain}>
									<div className={styles.tourName}>{tour.name}</div>
									<div className={styles.tourDate}>
										{formatYear(formatContractDate(tour.startDate))} ~{' '}
										{formatYear(formatContractDate(tour.endDate))}
									</div>
								</div>
							</div>
						))}
				</div>
			</div>
			<div className={styles.isOverSeasTourIncludeContainer}>
				<div className={styles.isOverSeasTourIncludeLabel}>해외 대회 포함 여부</div>
				<div className={styles.isOverSeasTourIncludeContent}>
					{dataForRender.isOverseas ? '해외대회 포함' : '해외대회 미포함'}
				</div>
			</div>
			<div className={styles.detailContractHeader}>상세 계약정보</div>
			{dataForRender.isGeneralContract && (
				<>
					<div className={styles.applyTaxContainer}>
						<div className={styles.applyTaxLabel}>세금 적용 여부</div>
						<div className={styles.applyTaxContent}>
							{dataForRender.generalFormData?.applyTax === 'exclude'
								? '세전'
								: '세후'}
						</div>
					</div>
					<div className={styles.payPeriodContainer}>
						<div className={styles.payPeriodLabel}>임금 지급 주기</div>
						<div className={styles.payPeriodContent}>
							{dataForRender.generalFormData?.payPeriod === 'weekly'
								? '주급'
								: '일시불제'}
						</div>
					</div>
					<div className={styles.periodSalaryContainer}>
						<div className={styles.periodSalaryLabel}>기간 급여액</div>
						<div className={styles.periodSalaryContent}>
							{formatNumber(dataForRender.generalFormData?.periodSalary)} 원
						</div>
					</div>
					<div className={styles.incentiveOptionContainer}>
						<div className={styles.incentiveOptionLabel}>{INCENTIVE_BASIS_LABEL}</div>
						<div className={styles.incentiveOptionContent}>
							{formatIncentiveTaxOptionLabel(
								dataForRender.generalFormData?.incentiveTaxOption,
							)}
						</div>
					</div>
					<div className={styles.incentiveSettingsContainer}>
						<div className={styles.incentiveSettingsLabel}>인센티브 금액</div>
						<div className={styles.incentiveSettingsContent}>
							{dataForRender.generalFormData?.incentiveSettings.map((setting) => {
								if (setting.percentage === null && setting.end === null) return null
								const displayRank =
									setting.id === 1
										? '우승 : '
										: setting.start +
											(setting.end !== null ? ` ~ ${setting.end} :  ` : '~ ')
								const displayPercentage =
									setting.percentage !== null
										? `  ${setting.percentage}%`
										: '미입력'
								return (
									<div key={setting.id} className={styles.incentiveDisplayItem}>
										<div>{displayRank}</div>
										<div>{displayPercentage}</div>
									</div>
								)
							})}
						</div>
					</div>
					<div className={styles.wearSponserHatContainer}>
						<div className={styles.wearSponserHatLabel}>스폰서 모자 착용 여부</div>
						<div className={styles.wearSponserHatContent}>
							{dataForRender.generalFormData?.sponsorCap ? '착용' : '미착용'}
						</div>
					</div>
				</>
			)}
			{dataForRender.isDomestic &&
				dataForRender.isOverseas &&
				dataForRender.domesticFormData &&
				dataForRender.overseasFormData && (
					<>
						<div className={styles.domesticOverseasSwapContainer}>
							<button
								className={
									domesticOverseasSwapContent
										? styles.seeDomesticChosen
										: styles.seeDomesticUnchosen
								}
								onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
								국내
							</button>
							<button
								className={
									domesticOverseasSwapContent
										? styles.seeOverseasUnchosen
										: styles.seeOverseasChosen
								}
								onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
								해외
							</button>
						</div>
						{domesticOverseasSwapContent && (
							<>
								<div className={styles.applyTaxContainer}>
									<div className={styles.applyTaxLabel}>세금 적용 여부</div>
									<div className={styles.applyTaxContent}>
										{dataForRender.domesticFormData?.applyTax === 'exclude'
											? '세전'
											: '세후'}
									</div>
								</div>
								<div className={styles.payPeriodContainer}>
									<div className={styles.payPeriodLabel}>임금 지급 주기</div>
									<div className={styles.payPeriodContent}>
										{dataForRender.domesticFormData?.payPeriod === 'weekly'
											? '주급'
											: '일시불제'}
									</div>
								</div>
								<div className={styles.cutPassContainer}>
									<div className={styles.cutPassLabel}>컷통과</div>
									<div className={styles.cutPassContent}>
										{formatNumber(dataForRender.domesticFormData?.cutPass)} 원
									</div>
								</div>
								<div className={styles.cutOutContainer}>
									<div className={styles.cutOutLabel}>컷탈락</div>
									<div className={styles.cutOutContent}>
										{formatNumber(dataForRender.domesticFormData?.cutOut)} 원
									</div>
								</div>
								<div className={styles.incentiveOptionContainer}>
									<div className={styles.incentiveOptionLabel}>
										{INCENTIVE_BASIS_LABEL}
									</div>
									<div className={styles.incentiveOptionContent}>
										{formatIncentiveTaxOptionLabel(
											dataForRender.domesticFormData?.incentiveTaxOption,
										)}
									</div>
								</div>
								<div className={styles.incentiveSettingsContainer}>
									<div className={styles.incentiveSettingsLabel}>
										인센티브 금액
									</div>
									<div className={styles.incentiveSettingsContent}>
										{dataForRender.domesticFormData?.incentiveSettings.map(
											(setting) => {
												if (
													setting.percentage === null &&
													setting.end === null
												)
													return null
												const displayRank =
													setting.id === 1
														? '우승 : '
														: setting.start +
															(setting.end !== null
																? ` ~ ${setting.end} :  `
																: '~ ')
												const displayPercentage =
													setting.percentage !== null
														? `  ${setting.percentage}%`
														: '미입력'
												return (
													<div
														key={setting.id}
														className={styles.incentiveDisplayItem}>
														<div>{displayRank}</div>
														<div>{displayPercentage}</div>
													</div>
												)
											},
										)}
									</div>
								</div>
								<div className={styles.wearSponserHatContainer}>
									<div className={styles.wearSponserHatLabel}>
										스폰서 모자 착용 여부
									</div>
									<div className={styles.wearSponserHatContent}>
										{dataForRender.domesticFormData?.sponsorCap
											? '착용'
											: '미착용'}
									</div>
								</div>
							</>
						)}
						{!domesticOverseasSwapContent && (
							<>
								<div className={styles.applyTaxContainer}>
									<div className={styles.applyTaxLabel}>세금 적용 여부</div>
									<div className={styles.applyTaxContent}>
										{dataForRender.overseasFormData?.applyTax === 'exclude'
											? '세전'
											: '세후'}
									</div>
								</div>
								<div className={styles.payPeriodContainer}>
									<div className={styles.payPeriodLabel}>임금 지급 주기</div>
									<div className={styles.payPeriodContent}>
										{dataForRender.overseasFormData?.payPeriod === 'weekly'
											? '주급'
											: '일시불제'}
									</div>
								</div>
								<div className={styles.cutPassContainer}>
									<div className={styles.cutPassLabel}>컷통과</div>
									<div className={styles.cutPassContent}>
										{formatNumber(dataForRender.overseasFormData?.cutPass)} 원
									</div>
								</div>
								<div className={styles.cutOutContainer}>
									<div className={styles.cutOutLabel}>컷탈락</div>
									<div className={styles.cutOutContent}>
										{formatNumber(dataForRender.overseasFormData?.cutOut)} 원
									</div>
								</div>
								<div className={styles.incentiveOptionContainer}>
									<div className={styles.incentiveOptionLabel}>
										{INCENTIVE_BASIS_LABEL}
									</div>
									<div className={styles.incentiveOptionContent}>
										{formatIncentiveTaxOptionLabel(
											dataForRender.overseasFormData?.incentiveTaxOption,
										)}
									</div>
								</div>
								<div className={styles.incentiveSettingsContainer}>
									<div className={styles.incentiveSettingsLabel}>
										인센티브 금액
									</div>
									<div className={styles.incentiveSettingsContent}>
										{dataForRender.overseasFormData?.incentiveSettings.map(
											(setting) => {
												if (
													setting.percentage === null &&
													setting.end === null
												)
													return null
												const displayRank =
													setting.id === 1
														? '우승 : '
														: setting.start +
															(setting.end !== null
																? ` ~ ${setting.end} :  `
																: '~ ')
												const displayPercentage =
													setting.percentage !== null
														? `  ${setting.percentage}%`
														: '미입력'
												return (
													<div
														key={setting.id}
														className={styles.incentiveDisplayItem}>
														<div>{displayRank}</div>
														<div>{displayPercentage}</div>
													</div>
												)
											},
										)}
									</div>
								</div>
								<div className={styles.wearSponserHatContainer}>
									<div className={styles.wearSponserHatLabel}>
										스폰서 모자 착용 여부
									</div>
									<div className={styles.wearSponserHatContent}>
										{dataForRender.overseasFormData?.sponsorCap
											? '착용'
											: '미착용'}
									</div>
								</div>
							</>
						)}
					</>
				)}

			{dataForRender.isOverseas &&
				dataForRender.overseasFormData &&
				!(dataForRender.isDomestic && dataForRender.isOverseas) && (
					<>
						<div className={styles.applyTaxContainer}>
							<div className={styles.applyTaxLabel}>세금 적용 여부</div>
							<div className={styles.applyTaxContent}>
								{dataForRender.overseasFormData?.applyTax === 'exclude'
									? '세전'
									: '세후'}
							</div>
						</div>
						<div className={styles.payPeriodContainer}>
							<div className={styles.payPeriodLabel}>임금 지급 주기</div>
							<div className={styles.payPeriodContent}>
								{dataForRender.overseasFormData?.payPeriod === 'weekly'
									? '주급'
									: '일시불제'}
							</div>
						</div>
						<div className={styles.cutPassContainer}>
							<div className={styles.cutPassLabel}>컷통과</div>
							<div className={styles.cutPassContent}>
								{formatNumber(dataForRender.overseasFormData?.cutPass)} 원
							</div>
						</div>
						<div className={styles.cutOutContainer}>
							<div className={styles.cutOutLabel}>컷탈락</div>
							<div className={styles.cutOutContent}>
								{formatNumber(dataForRender.overseasFormData?.cutOut)} 원
							</div>
						</div>
						<div className={styles.incentiveOptionContainer}>
							<div className={styles.incentiveOptionLabel}>
								{INCENTIVE_BASIS_LABEL}
							</div>
							<div className={styles.incentiveOptionContent}>
								{formatIncentiveTaxOptionLabel(
									dataForRender.overseasFormData?.incentiveTaxOption,
								)}
							</div>
						</div>
						<div className={styles.incentiveSettingsContainer}>
							<div className={styles.incentiveSettingsLabel}>인센티브 금액</div>
							<div className={styles.incentiveSettingsContent}>
								{dataForRender.overseasFormData?.incentiveSettings.map(
									(setting) => {
										if (setting.percentage === null && setting.end === null)
											return null
										const displayRank =
											setting.id === 1
												? '우승 : '
												: setting.start +
													(setting.end !== null
														? ` ~ ${setting.end} :  `
														: '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `  ${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<div>{displayRank}</div>
												<div>{displayPercentage}</div>
											</div>
										)
									},
								)}
							</div>
						</div>
						<div className={styles.wearSponserHatContainer}>
							<div className={styles.wearSponserHatLabel}>스폰서 모자 착용 여부</div>
							<div className={styles.wearSponserHatContent}>
								{dataForRender.overseasFormData?.sponsorCap ? '착용' : '미착용'}
							</div>
						</div>
					</>
				)}
			{dataForRender.isDomestic &&
				dataForRender.domesticFormData &&
				!(dataForRender.isOverseas && dataForRender.isDomestic) && (
					<>
						<div className={styles.applyTaxContainer}>
							<div className={styles.applyTaxLabel}>세금 적용 여부</div>
							<div className={styles.applyTaxContent}>
								{dataForRender.domesticFormData?.applyTax === 'exclude'
									? '세전'
									: '세후'}
							</div>
						</div>
						<div className={styles.payPeriodContainer}>
							<div className={styles.payPeriodLabel}>임금 지급 주기</div>
							<div className={styles.payPeriodContent}>
								{dataForRender.domesticFormData?.payPeriod === 'weekly'
									? '주급'
									: '일시불제'}
							</div>
						</div>
						<div className={styles.cutPassContainer}>
							<div className={styles.cutPassLabel}>컷통과</div>
							<div className={styles.cutPassContent}>
								{formatNumber(dataForRender.domesticFormData?.cutPass)} 원
							</div>
						</div>
						<div className={styles.cutOutContainer}>
							<div className={styles.cutOutLabel}>컷탈락</div>
							<div className={styles.cutOutContent}>
								{formatNumber(dataForRender.domesticFormData?.cutOut)} 원
							</div>
						</div>
						<div className={styles.incentiveOptionContainer}>
							<div className={styles.incentiveOptionLabel}>
								{INCENTIVE_BASIS_LABEL}
							</div>
							<div className={styles.incentiveOptionContent}>
								{formatIncentiveTaxOptionLabel(
									dataForRender.domesticFormData?.incentiveTaxOption,
								)}
							</div>
						</div>
						<div className={styles.incentiveSettingsContainer}>
							<div className={styles.incentiveSettingsLabel}>인센티브 금액</div>
							<div className={styles.incentiveSettingsContent}>
								{dataForRender.domesticFormData?.incentiveSettings.map(
									(setting) => {
										if (setting.percentage === null && setting.end === null)
											return null
										const displayRank =
											setting.id === 1
												? '우승 : '
												: setting.start +
													(setting.end !== null
														? ` ~ ${setting.end} :  `
														: '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `  ${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<div>{displayRank}</div>
												<div>{displayPercentage}</div>
											</div>
										)
									},
								)}
							</div>
						</div>
						<div className={styles.wearSponserHatContainer}>
							<div className={styles.wearSponserHatLabel}>스폰서 모자 착용 여부</div>
							<div className={styles.wearSponserHatContent}>
								{dataForRender.domesticFormData?.sponsorCap ? '착용' : '미착용'}
							</div>
						</div>
					</>
				)}
			<div className={styles.authorHeader}>작성자 정보</div>
			<div className={styles.author}>
				{authStatus.user.name + ' / ' + dataForRender.userAssociation}
			</div>
		</div>
	)
}

export default PreviewModal
